import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import MainAdmin from "./MainAdmin";
import socket from "../../socket/socket";
import {
  fetch_cc_history,
  admin_cc_refund,
  admin_cc_retry_refund,
  admin_cc_reconcile,
} from "../../api/admin/creditCardBillApi";
import { admin_UserList } from "../../api/userAuth";
import { formatINR } from "../../utils/validators";
import { autoDateFormat } from "../../utils/autoDateFormate";
import { useNotify } from "../../context/NotifyContext";
import { useTxnDocActions } from "../../hooks/useTxnDocActions";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  RowActions,
  StatusBadge,
  useListingState,
  CREDIT_CARD_BILL_STATUS_OPTIONS,
} from "../../components/listing";

const TOTAL_COLS = 16;

const maskCard = (last4) => (last4 ? `XXXX ${String(last4).slice(-4)}` : "");

const getDescription = (r) => {
  if (r?.status === "FAILED") {
    return r?.failureReason || r?.wingpayResponse?.message || "Payment failed";
  }
  if (r?.status === "PENDING") return "Payment is being processed";
  return r?.wingpayResponse?.message || "Payment successful";
};

const CreditCardBillPaymentHisotryAdmin = () => {
  const { notify } = useNotify();
  const { viewTxn, printTxn, pdfTxn } = useTxnDocActions("bbps");
  const role = String(useSelector((s) => s.auth?.userInfo?.role) || "");
  const isSuperAdmin = role === "super_admin";

  const listing = useListingState({
    sortBy: "date",
    sortDir: "desc",
    limit: 20,
  });

  const [transactions, setTransactions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([{ value: "", label: "All Users" }]);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionReason, setActionReason] = useState("");

  // ================= USERS DROPDOWN =================
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await admin_UserList({ page: 1, limit: 200 });
      if (cancelled) return;
      if (res?.success) {
        const users = res.data?.users || [];
        setUserOptions([
          { value: "", label: "All Users" },
          ...users.map((u) => ({
            value: u._id,
            label: u.userName ? `${u.userName}${u.name ? ` — ${u.name}` : ""}` : u.name || u._id,
          })),
        ]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ================= FETCH HISTORY =================
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        page: listing.page,
        limit: listing.limit,
        sortBy: listing.sortBy,
        sortDir: listing.sortDir,
      };
      if (listing.startDate) params.startDate = listing.startDate;
      if (listing.endDate) params.endDate = listing.endDate;
      if (listing.debouncedSearch) params.search = listing.debouncedSearch;
      if (listing.status) params.status = listing.status;
      if (listing.userId) params.userId = listing.userId; // admin filter

      const res = await fetch_cc_history(params);

      if (res?.success) {
        const d = res?.data;
        setTransactions(d?.utilityPayments || []);
        setTotalPages(d?.pagination?.totalPages || 1);
        setTotalCount(d?.pagination?.total || 0);
        setTotalAmount(Number(d?.totalAmount) || 0);
      } else {
        setTransactions([]);
        setTotalPages(1);
        setTotalCount(0);
        setTotalAmount(0);
        notify(res?.message || "Failed to load transactions", "error");
      }
    } catch {
      setTransactions([]);
      setTotalPages(1);
      setTotalCount(0);
      setTotalAmount(0);
      notify("Failed to load transactions", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.startDate,
    listing.endDate,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
    listing.userId,
  ]);

  useEffect(() => {
    const onWalletChanged = () => fetchTransactions();
    socket.on("wallet_changed", onWalletChanged);
    return () => socket.off("wallet_changed", onWalletChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.startDate,
    listing.endDate,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
    listing.userId,
  ]);

  return (
    <MainAdmin>
      <div className="mt-1">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/admin/dashboard" },
            { label: "Transactions" },
            { label: "Credit Card Bill Payment" },
          ]}
        />

        <div className="ds-card mt-2 mb-2">
          <h2 className="text-xs font-semibold capitalize text-[var(--text-dark)] m-0">
            Credit Card Bill Payment Transactions
          </h2>
        </div>

        <div className="ds-card text-sm">
          <ListingFilters
            onClear={listing.clearFilters}
            startDate={listing.startDate}
            endDate={listing.endDate}
            onStartDate={listing.setStartDate}
            onEndDate={listing.setEndDate}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search Order ID, Mobile, Card, Ref"
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={CREDIT_CARD_BILL_STATUS_OPTIONS}
            userId={listing.userId}
            onUserId={listing.setUserId}
            userOptions={userOptions}
            // onExport={() =>
            //   exportTable(
            //     "credit-card-bill-payments",
            //     [
            //       { label: "#", value: (_r, i) => (listing.page - 1) * listing.limit + i + 1 },
            //       { label: "Date", value: (r) => (r?.createdAt ? autoDateFormat(r.createdAt) : "") },
            //       { label: "Order ID", value: (r) => r?.orderId ?? r?.txnId ?? "" },
            //       { label: "User", value: (r) => r?.userId?.userName || r?.userId?.name || "" },
            //       { label: "User Mobile", value: (r) => r?.userId?.phone ?? "" },
            //       { label: "Customer Name", value: (r) => r?.customerName ?? "" },
            //       { label: "Customer Mobile", value: (r) => r?.mobile ?? "" },
            //       { label: "Card No.", value: (r) => maskCard(r?.last4DigitCardNumber) },
            //       { label: "Amount", value: (r) => r?.amount ?? 0 },
            //       { label: "Charges", value: (r) => r?.charges ?? 0 },
            //       { label: "Total Debit", value: (r) => r?.totalDebit ?? 0 },
            //       { label: "Operator Ref ID", value: (r) => r?.operatorRefId ?? "" },
            //       { label: "API Txn ID", value: (r) => r?.apiTxnId ?? "" },
            //       { label: "Description", value: (r) => getDescription(r) },
            //       { label: "Status", value: (r) => r?.status ?? "" },
            //     ],
            //     transactions,
            //     notify
            //   )
            // }
            actions={
              <button
                type="button"
                onClick={fetchTransactions}
                disabled={loading}
                className="ds-action-btn"
              >
                <ArticleOutlinedIcon fontSize="small" /> {loading ? "LOADING..." : "LOAD DATA"}
              </button>
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[1300px]">
              <thead>
                <tr>
                  <th>#</th>
                  <SortableTh
                    label="Date"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Order ID</th>
                  <th>User</th>
                  <th>Customer</th>
                  <th>Customer Mobile</th>
                  <th>Card No.</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Charges</th>
                  <th>Total Debit</th>
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Refund</th>
                  {/* <th>Provider</th> */}
                  <th>Time</th>
                  <th>Ref / Txn</th>
                  <th className="ds-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && transactions.length === 0 ? (
                  <tr>
                    <td colSpan={TOTAL_COLS} className="text-center text-[var(--text-muted)]">
                      Loading...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={TOTAL_COLS} className="text-center text-[var(--text-muted)]">
                      NO DATA AVAILABLE IN TABLE
                    </td>
                  </tr>
                ) : (
                  transactions.map((row, index) => (
                    <tr key={row._id || index}>
                      <td>{(listing.page - 1) * listing.limit + index + 1}</td>
                      <td>{row.createdAt ? autoDateFormat(row.createdAt) : "—"}</td>
                      <td>
                        <CopyText value={row.orderId ?? row.txnId} label="Order ID" />
                      </td>
                      <td>
                        <div>{row.userId?.userName || row.userId?.name || "—"}</div>
                        <div className="text-[11px] text-[var(--text-muted)]">
                          {row.userId?.phone || ""}
                        </div>
                      </td>
                      <td>{row.customerName || "—"}</td>
                      <td>{row.mobile || "—"}</td>
                      <td>{maskCard(row.last4DigitCardNumber) || "—"}</td>
                      <td className="ds-td-amount">{formatINR(row.amount)}</td>
                      <td>{formatINR(row.charges ?? 0)}</td>
                      <td>{formatINR(row.totalDebit ?? 0)}</td>
                      <td>
                        <StatusBadge status={row.status} />
                        {row.refunded ? (
                          <div className="text-[10px] text-[var(--text-muted)]">Refunded</div>
                        ) : null}
                      </td>
                      <td>
                        <div>
                          {row.refunded || row.status === "REFUNDED"
                            ? "Yes"
                            : row.status === "REFUND_PENDING"
                            ? "Pending"
                            : "No"}
                        </div>
                        {row.refundTxnId ? (
                          <div className="text-[10px] text-[var(--text-muted)]">{row.refundTxnId}</div>
                        ) : null}
                        {row.refundedAt ? (
                          <div className="text-[10px] text-[var(--text-muted)]">
                            {autoDateFormat(row.refundedAt)}
                          </div>
                        ) : null}
                      </td>
                      {/* <td className="max-w-[180px] text-[11px] text-[var(--text-muted)]">
                        {row.providerFailureMessage || row.failureReason || "—"}
                      </td> */}
                      <td>
                        {row.createdAt ? new Date(row.createdAt).toLocaleTimeString() : "—"}
                      </td>
                      <td>
                        <div className="text-[12px]">
                          {row.operatorRefId ? (
                            <CopyText value={row.operatorRefId} label="Operator Ref ID" />
                          ) : (
                            "—"
                          )}
                        </div>
                        <div className="text-[11px] text-[var(--text-muted)]">
                          {row.apiTxnId || ""}
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-col gap-1">
                          <RowActions
                            onView={() => viewTxn(row)}
                            onPdf={() => pdfTxn(row)}
                            onPrint={() => printTxn(row)}
                          />
                          {isSuperAdmin && row.status === "REFUND_PENDING" ? (
                            <button
                              type="button"
                              className="ds-action-btn"
                              onClick={() =>
                                setActionDialog({ type: "retry", row })
                              }
                            >
                              Retry Refund
                            </button>
                          ) : null}
                          {isSuperAdmin &&
                          row.status === "FAILED" &&
                          !row.refunded &&
                          (row.walletDebited || Number(row.openingBalance) > 0) ? (
                            <button
                              type="button"
                              className="ds-action-btn"
                              onClick={() => {
                                setActionReason("");
                                setActionDialog({ type: "refund", row });
                              }}
                            >
                              Manual Refund
                            </button>
                          ) : null}
                          {isSuperAdmin &&
                          (row.status === "PENDING" ||
                            row.status === "RECONCILIATION_REQUIRED") ? (
                            <button
                              type="button"
                              className="ds-action-btn"
                              onClick={() => {
                                setActionReason("");
                                setActionDialog({ type: "reconcile", row });
                              }}
                            >
                              Reconcile
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="ds-table-total">
                  <td colSpan={7} className="ds-table-total__label">
                    Total Amount (Success)
                  </td>
                  <td className="ds-table-total__value">{formatINR(totalAmount)}</td>
                  <td colSpan={8}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalCount}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[10, 20, 50, 100]}
          />
        </div>
      </div>

      <Dialog
        open={Boolean(actionDialog)}
        onClose={() => !actionBusy && setActionDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {actionDialog?.type === "retry"
            ? "Retry refund"
            : actionDialog?.type === "reconcile"
            ? "Reconcile as failed"
            : "Manual refund"}
        </DialogTitle>
        <DialogContent>
          <p className="text-sm text-[var(--text-muted)] mb-2">
            Order {actionDialog?.row?.orderId}. Refund amount is taken from the
            original debit only.
          </p>
          {actionDialog?.type !== "retry" ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setActionDialog(null)}>
            Cancel
          </Button>
          <Button
            disabled={actionBusy}
            variant="contained"
            onClick={async () => {
              if (actionDialog?.type !== "retry" && actionReason.trim().length < 5) {
                notify("Enter a reason (min 5 characters)", "error");
                return;
              }
              setActionBusy(true);
              const id = actionDialog.row._id;
              let res;
              if (actionDialog.type === "retry") {
                res = await admin_cc_retry_refund(id, actionReason || "retry");
              } else if (actionDialog.type === "reconcile") {
                res = await admin_cc_reconcile(id, actionReason.trim());
              } else {
                res = await admin_cc_refund(id, actionReason.trim());
              }
              setActionBusy(false);
              if (res.success) {
                notify(res.message || "Done", "success");
                setActionDialog(null);
                fetchTransactions();
              } else {
                notify(res.message || "Action failed", "error");
              }
            }}
          >
            {actionBusy ? "Working..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainAdmin>
  );
};

export default CreditCardBillPaymentHisotryAdmin;